/**
 * Phase 6.3 — Promo Agent (autonomous campaign proposal queue).
 *
 * SA clicks [Generate proposals] → backend calls Claude → returns 0–4
 * structured campaign ideas. SA edits/approves/rejects each one.
 * Approved proposals become scheduled BOOSTs at the suggested ET time.
 *
 * Also surfaces "Yesterday's top-converting copy" for inspiration.
 *
 * Feb 2026 — Promo Agent v2:
 *  - Manual Directive textarea passed to the backend on Generate.
 *  - Each proposal exposes a [View Target Cohort List] button that opens
 *    a modal showing the resolved criteria, total recipients, and a
 *    20-user sample. SA must Confirm the list before Approve unlocks.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, Sparkles, Check, X, TrendingUp, Loader2, Users, ShieldCheck, Lock } from 'lucide-react';
import { Navbar } from '../../components/Navbar';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

export default function PromoAgentPage() {
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [winners, setWinners] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [edits, setEdits] = useState({});
  // Feb 2026 — Promo Agent v2 state.
  const [directive, setDirective] = useState('');
  const [targetList, setTargetList] = useState(null);       // resolved list payload for the modal
  const [targetListLoading, setTargetListLoading] = useState(false);
  const [confirmingList, setConfirmingList] = useState(false);
  // Feb 2026 — channel selector state. `channels[proposalId] = 'both' | 'email' | 'push'`
  // Default 'both' when a proposal is first rendered.
  const [channels, setChannels] = useState({});
  const channelOf = (pid) => channels[pid] || 'both';
  const setChannel = (pid, val) => setChannels((prev) => ({ ...prev, [pid]: val }));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/promo-agent/proposals`, { withCredentials: true });
      setProposals(r.data.proposals || []);
      setWinners(r.data.winners || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load proposals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const body = directive.trim() ? { directive: directive.trim() } : {};
      const r = await axios.post(`${API_URL}/api/admin/promo-agent/generate`, body, { withCredentials: true });
      if (r.data.saved_count === 0) {
        toast.info(r.data.summary || 'No proposals warranted right now.');
      } else {
        toast.success(`${r.data.saved_count} proposal(s) ready for review`);
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Agent failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async (proposalId) => {
    const e = edits[proposalId] || {};
    const chSel = channelOf(proposalId);
    const channelsFlags = {
      email: chSel !== 'push',
      push: chSel !== 'email',
    };
    try {
      const r = await axios.post(`${API_URL}/api/admin/promo-agent/approve`, {
        proposal_id: proposalId,
        ...e,
        channels: channelsFlags,
      }, { withCredentials: true });
      toast.success(`Scheduled ${r.data.recipients} sends for ${new Date(r.data.scheduled_at).toLocaleString()}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approve failed');
    }
  };

  const handleReject = async (proposalId) => {
    try {
      await axios.post(`${API_URL}/api/admin/promo-agent/reject`, { proposal_id: proposalId }, { withCredentials: true });
      toast.success('Proposal dismissed');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reject failed');
    }
  };

  const openTargetList = async (proposalId) => {
    setTargetList({ proposal_id: proposalId, loading: true });
    setTargetListLoading(true);
    try {
      const r = await axios.get(
        `${API_URL}/api/admin/promo-agent/proposals/${proposalId}/target-list`,
        { withCredentials: true },
      );
      setTargetList({ ...r.data, loading: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load target list');
      setTargetList(null);
    } finally {
      setTargetListLoading(false);
    }
  };

  const confirmTargetList = async () => {
    if (!targetList?.proposal_id) return;
    setConfirmingList(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/promo-agent/proposals/${targetList.proposal_id}/confirm-list`,
        {},
        { withCredentials: true },
      );
      toast.success('Target list confirmed. You can now approve & schedule.');
      // Reflect the confirmation locally so the Approve button unlocks immediately.
      setProposals((prev) => prev.map((p) => (
        p.id === targetList.proposal_id
          ? { ...p, list_confirmed_at: r.data.list_confirmed_at, list_confirmed_by: r.data.list_confirmed_by }
          : p
      )));
      setTargetList(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Confirm failed');
    } finally {
      setConfirmingList(false);
    }
  };

  const updateEdit = (id, field, value) => {
    setEdits((p) => ({ ...p, [id]: { ...(p[id] || {}), [field]: value } }));
  };

  return (
    <div className="min-h-screen bg-[#050003] scanline-overlay dispatch-safe-top">
      <Navbar />
      <div className="p-4 sm:p-8">
      <div className="max-w-5xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="text-[#A3C7A8] hover:text-[#39FF14] font-mono text-xs uppercase tracking-wider flex items-center gap-2 mb-4"
          data-testid="pa-back-button"
        >
          <ArrowLeft className="w-4 h-4" /> // BACK
        </button>

        <div className="border border-[#2A0F3D] bg-[#0C0612] p-6 mb-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-[#39FF14] font-mono text-xl font-bold uppercase tracking-wider">
                {'>'} PROMO_AGENT_
              </h1>
              <p className="text-[#A3C7A8] font-mono text-xs mt-2">
                // Claude-powered campaign proposals. Reads inventory + cohorts + history.
                Approved proposals become scheduled BOOSTs.
              </p>
            </div>
            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="bg-[#39FF14] text-black hover:bg-[#2bcc10] font-mono text-xs uppercase tracking-wider rounded-none"
              data-testid="pa-generate-button"
            >
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {generating ? 'Thinking...' : 'Generate proposals'}
            </Button>
          </div>
          {/* Feb 2026 — Manual Directive. Optional free-text steering
              instruction. When populated, the LLM MUST honor it as the
              primary targeting rule (see promo_agent.SYSTEM_PROMPT). */}
          <div className="mt-4 border-t border-[#2A0F3D] pt-4">
            <Label className="text-[#D4823B] font-mono text-[10px] uppercase tracking-widest flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> // MANUAL DIRECTIVE (optional)
            </Label>
            <Textarea
              value={directive}
              onChange={(ev) => setDirective(ev.target.value)}
              rows={2}
              maxLength={2000}
              placeholder="e.g. Target NJ customers who haven't ordered in 30+ days, no points."
              className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none mt-2 text-sm"
              data-testid="pa-directive-input"
            />
            <div className="text-[10px] font-mono text-[#6B8E73] mt-1">
              // {directive.length} / 2000 chars. Leave blank for a purely data-driven pick.
            </div>
          </div>
        </div>

        {/* Top-converting copy */}
        {winners.length > 0 && (
          <div className="border border-[#2A0F3D] bg-[#0C0612] p-5 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-[#D4823B]" />
              <h2 className="text-[#D4823B] font-mono text-sm uppercase tracking-widest">{`// Top converters (last 7d)`}</h2>
            </div>
            <div className="space-y-2">
              {winners.map((w) => (
                <div key={w.grant_id} className="flex items-center justify-between gap-3 text-sm font-mono border-l-2 border-[#D4823B]/60 pl-3 py-1">
                  <div className="text-[#A3C7A8] truncate flex-1">{w.subject || '(no subject)'}</div>
                  <div className="text-xs text-[#6B8E73] whitespace-nowrap flex items-center gap-2">
                    <span>{w.cohort_label || '—'}</span>
                    <span>·</span>
                    <span>{w.sent} sent</span>
                    <span>·</span>
                    <span className="text-[#82937B]">{w.open_rate}% open</span>
                    {w.orders_attributed > 0 && (
                      <>
                        <span>·</span>
                        <span className="text-[#39FF14] font-bold" data-testid={`pa-winner-conv-${w.grant_id}`}>
                          {w.conversion_rate_pct}% conv
                        </span>
                        <span>·</span>
                        <span className="text-[#39FF14]">${Number(w.revenue_attributed ?? 0).toFixed(0)}</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending proposals */}
        {proposals.length === 0 && !loading && (
          <div className="border border-[#2A0F3D] bg-[#0C0612] p-8 text-center">
            <p className="text-[#A3C7A8] font-mono text-sm">
              {`// No pending proposals. Click "Generate proposals" to ask the agent.`}
            </p>
          </div>
        )}

        <div className="space-y-4">
          {proposals.map((p) => {
            const e = edits[p.id] || {};
            return (
              <div key={p.id} className="border border-[#39FF14]/40 bg-[#0C0612] p-5" data-testid={`pa-proposal-${p.id}`}>
                <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                  <div className="text-[#39FF14] font-mono text-xs uppercase tracking-wider">
                    {p.cohort_key} · {(() => {
                      const editedPts = edits[p.id]?.points_grant;
                      const pts = editedPts !== undefined ? editedPts : p.points_grant;
                      return pts > 0 ? <span className="text-[#FFD700]">+{pts}PTS</span> : 'NO POINTS';
                    })()} · {(edits[p.id]?.suggested_send_time_et) || p.suggested_send_time_et} ET
                  </div>
                  {/* Feb 2026 — Points-liability estimate. Updates live as the
                      operator edits the points field so they see total cost
                      BEFORE approving. Cohort size comes from the backend
                      enrichment (live count, not a stale stored value). */}
                  <div className="font-mono text-[10px] uppercase tracking-widest flex items-center gap-2" data-testid={`pa-cost-${p.id}`}>
                    <span className="text-[#6B8E73]">
                      RECIPIENTS: <span className="text-[#A3C7A8]">{p.estimated_recipients ?? '—'}</span>
                    </span>
                    {(() => {
                      const editedPts = edits[p.id]?.points_grant;
                      const pts = editedPts !== undefined ? editedPts : (p.points_grant || 0);
                      const cost = (p.estimated_recipients || 0) * pts;
                      if (pts === 0) return null;
                      return (
                        <span className="text-[#6B8E73]">
                          · COST: <span className="text-[#FFD700] font-bold">{cost.toLocaleString()} pts</span>
                        </span>
                      );
                    })()}
                    <span className="text-[#6B8E73]">· {p.model}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-[#6B8E73] font-mono text-[10px] uppercase tracking-widest">Email · subject</Label>
                    <Input
                      defaultValue={p.subject}
                      onChange={(ev) => updateEdit(p.id, 'subject', ev.target.value)}
                      className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none"
                      data-testid={`pa-subject-${p.id}`}
                    />
                  </div>
                  <div>
                    <Label className="text-[#6B8E73] font-mono text-[10px] uppercase tracking-widest">Email · inbox preview text</Label>
                    <Input
                      defaultValue={p.email_preview || ''}
                      onChange={(ev) => updateEdit(p.id, 'email_preview', ev.target.value)}
                      className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none"
                      placeholder="Snippet shown under subject in inbox"
                      data-testid={`pa-email-preview-${p.id}`}
                    />
                  </div>
                  <div>
                    <Label className="text-[#6B8E73] font-mono text-[10px] uppercase tracking-widest">Email · body copy</Label>
                    <Textarea
                      defaultValue={p.email_body || ''}
                      onChange={(ev) => updateEdit(p.id, 'email_body', ev.target.value)}
                      rows={4}
                      className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none"
                      placeholder="Use {first_name} to personalize"
                      data-testid={`pa-email-body-${p.id}`}
                    />
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-[#6B8E73] font-mono text-[10px] uppercase tracking-widest">Push · title</Label>
                      <Input
                        defaultValue={p.push_title}
                        onChange={(ev) => updateEdit(p.id, 'push_title', ev.target.value)}
                        className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none"
                        data-testid={`pa-push-title-${p.id}`}
                      />
                    </div>
                    <div>
                      <Label className="text-[#6B8E73] font-mono text-[10px] uppercase tracking-widest">Send time ET (HH:MM)</Label>
                      <Input
                        defaultValue={p.suggested_send_time_et}
                        onChange={(ev) => updateEdit(p.id, 'suggested_send_time_et', ev.target.value)}
                        className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none"
                        data-testid={`pa-send-time-${p.id}`}
                      />
                    </div>
                    <div>
                      <Label className="text-[#6B8E73] font-mono text-[10px] uppercase tracking-widest">
                        Points grant <span className="text-[#4B6B52] normal-case">(0 = no points)</span>
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        max="5000"
                        step="50"
                        defaultValue={p.points_grant || 0}
                        onChange={(ev) => updateEdit(p.id, 'points_grant', parseInt(ev.target.value, 10) || 0)}
                        className="bg-[#050003] border-[#2A0F3D] text-[#FFD700] font-mono rounded-none font-bold"
                        data-testid={`pa-points-${p.id}`}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#6B8E73] font-mono text-[10px] uppercase tracking-widest">Push · body</Label>
                    <Textarea
                      defaultValue={p.push_body}
                      onChange={(ev) => updateEdit(p.id, 'push_body', ev.target.value)}
                      rows={2}
                      className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none"
                      data-testid={`pa-push-body-${p.id}`}
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[#6B8E73] font-mono text-[10px] uppercase tracking-widest">CTA button label</Label>
                      <Input
                        defaultValue={p.cta_label || 'Shop the menu'}
                        onChange={(ev) => updateEdit(p.id, 'cta_label', ev.target.value)}
                        className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none"
                        data-testid={`pa-cta-label-${p.id}`}
                      />
                    </div>
                    <div>
                      <Label className="text-[#6B8E73] font-mono text-[10px] uppercase tracking-widest">CTA destination (URL path)</Label>
                      <Input
                        defaultValue={p.cta_url_path || '/shop'}
                        onChange={(ev) => updateEdit(p.id, 'cta_url_path', ev.target.value)}
                        className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none"
                        data-testid={`pa-cta-url-${p.id}`}
                      />
                    </div>
                  </div>
                  <div className="text-[#A3C7A8] font-mono text-xs italic border-l-2 border-[#A3C7A8]/30 pl-3">
                    {`// Reasoning: ${p.reasoning || '—'}`}
                  </div>
                  {/* Feb 2026 — Promo Agent v2. Criteria (LLM-authored, plain-English) +
                      target list confirmation gate. */}
                  <div className="border-t border-[#2A0F3D] pt-3 mt-2 space-y-2">
                    <div className="flex items-start gap-2 text-xs font-mono">
                      <Users className="w-3 h-3 text-[#D4823B] mt-0.5 shrink-0" />
                      <div>
                        <span className="text-[#D4823B] uppercase tracking-widest text-[10px]">// TARGET CRITERIA</span>
                        <div className="text-[#A3C7A8] mt-0.5" data-testid={`pa-criteria-${p.id}`}>
                          {p.cohort_criteria || <span className="text-[#6B8E73] italic">(no criteria emitted)</span>}
                        </div>
                        {p.directive_snapshot && (
                          <div className="text-[10px] text-[#6B8E73] mt-1">
                            // directive at generate-time: <span className="text-[#A3C7A8]">{p.directive_snapshot}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        onClick={() => openTargetList(p.id)}
                        variant="ghost"
                        className="text-[#D4823B] hover:bg-[#D4823B]/10 font-mono text-xs uppercase tracking-wider rounded-none border border-[#D4823B]/50"
                        data-testid={`pa-view-target-${p.id}`}
                      >
                        <Users className="w-3 h-3 mr-1" /> View target cohort list
                      </Button>
                      {p.list_confirmed_at ? (
                        <span
                          className="text-[10px] font-mono uppercase tracking-widest text-[#39FF14] flex items-center gap-1"
                          data-testid={`pa-list-confirmed-${p.id}`}
                        >
                          <ShieldCheck className="w-3 h-3" />
                          LIST CONFIRMED · {p.list_confirmed_by || 'operator'}
                        </span>
                      ) : (
                        <span
                          className="text-[10px] font-mono uppercase tracking-widest text-[#FF3B30] flex items-center gap-1"
                          data-testid={`pa-list-unconfirmed-${p.id}`}
                        >
                          <Lock className="w-3 h-3" /> LIST NOT YET CONFIRMED
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2 mt-4">
                  {/* Feb 2026 — Channel selector. Operator picks Email, Push,
                      or Both. Default = Both. Backend gates the scheduler
                      payload on this flag so only the chosen channels fire. */}
                  <div className="flex items-center gap-2 flex-wrap" data-testid={`pa-channel-selector-${p.id}`}>
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#4B6B52]">
                      SEND VIA:
                    </span>
                    {['both', 'email', 'push'].map((val) => {
                      const active = channelOf(p.id) === val;
                      const label = val === 'both' ? 'Email + Push' : val === 'email' ? 'Email only' : 'Push only';
                      return (
                        <button
                          key={val}
                          type="button"
                          onClick={() => setChannel(p.id, val)}
                          className={`px-2 py-1 border font-mono text-[10px] uppercase tracking-widest transition-colors ${
                            active
                              ? 'border-[#39FF14] bg-[#39FF14]/10 text-[#39FF14]'
                              : 'border-[#2A0F3D] text-[#6B8E73] hover:border-[#39FF14]/50 hover:text-[#EAE7E1]'
                          }`}
                          data-testid={`pa-channel-${val}-${p.id}`}
                          aria-pressed={active}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => handleApprove(p.id)}
                      disabled={!p.list_confirmed_at}
                      title={!p.list_confirmed_at ? 'Confirm the target cohort list first' : ''}
                      className="bg-[#39FF14] text-black hover:bg-[#2bcc10] font-mono text-xs uppercase tracking-wider rounded-none disabled:bg-[#1A1A1A] disabled:text-[#4B6B52] disabled:cursor-not-allowed"
                      data-testid={`pa-approve-${p.id}`}
                    >
                      <Check className="w-3 h-3 mr-1" /> Approve & schedule
                    </Button>
                  <Button
                    onClick={() => handleReject(p.id)}
                    variant="ghost"
                    className="text-[#FF3B30] hover:bg-red-500/10 font-mono text-xs uppercase tracking-wider"
                    data-testid={`pa-reject-${p.id}`}
                  >
                    <X className="w-3 h-3 mr-1" /> Dismiss
                  </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Feb 2026 — Target Cohort List modal. Shown when the SA clicks
          [View target cohort list] on a proposal. Displays criteria,
          total count, 20-user sample table, and confirm CTA. Backend
          enforces the same guard so operators can't bypass this via
          curl. */}
      {targetList && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="pa-target-modal"
          onClick={() => (!confirmingList ? setTargetList(null) : null)}
        >
          <div
            className="bg-[#0C0612] border border-[#39FF14]/60 max-w-3xl w-full max-h-[85vh] overflow-y-auto"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="border-b border-[#2A0F3D] p-4 flex items-center justify-between gap-4">
              <h2 className="text-[#39FF14] font-mono text-sm uppercase tracking-widest flex items-center gap-2">
                <Users className="w-4 h-4" /> // TARGET COHORT LIST
              </h2>
              <button
                onClick={() => (!confirmingList ? setTargetList(null) : null)}
                className="text-[#A3C7A8] hover:text-[#FF3B30] font-mono text-xs"
                data-testid="pa-target-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {targetList.loading || targetListLoading ? (
              <div className="p-8 text-center text-[#A3C7A8] font-mono text-xs">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                Resolving cohort...
              </div>
            ) : (
              <div className="p-5 space-y-4">
                <div>
                  <Label className="text-[#D4823B] font-mono text-[10px] uppercase tracking-widest">// Criteria</Label>
                  <div className="text-[#A3C7A8] font-mono text-sm mt-1" data-testid="pa-target-criteria">
                    {targetList.cohort_criteria || <span className="italic text-[#6B8E73]">(no criteria)</span>}
                  </div>
                  {targetList.directive_snapshot && (
                    <div className="text-[10px] font-mono text-[#6B8E73] mt-1">
                      // directive: <span className="text-[#A3C7A8]">{targetList.directive_snapshot}</span>
                    </div>
                  )}
                  {Object.keys(targetList.cohort_filters || {}).length > 0 && (
                    <div className="text-[10px] font-mono text-[#6B8E73] mt-1">
                      // filters: <span className="text-[#A3C7A8]">{JSON.stringify(targetList.cohort_filters)}</span>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs font-mono">
                  <div className="border border-[#2A0F3D] p-3">
                    <div className="text-[#6B8E73] text-[10px] uppercase tracking-widest">Total recipients</div>
                    <div className="text-[#39FF14] text-2xl font-bold" data-testid="pa-target-total">
                      {targetList.total_recipients}
                    </div>
                  </div>
                  <div className="border border-[#2A0F3D] p-3">
                    <div className="text-[#6B8E73] text-[10px] uppercase tracking-widest">Points/recipient</div>
                    <div className="text-[#FFD700] text-2xl font-bold">{targetList.points_grant}</div>
                  </div>
                  <div className="border border-[#2A0F3D] p-3">
                    <div className="text-[#6B8E73] text-[10px] uppercase tracking-widest">Points liability</div>
                    <div className="text-[#FFD700] text-2xl font-bold" data-testid="pa-target-cost">
                      {(targetList.points_cost_estimate || 0).toLocaleString()}
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-[#D4823B] font-mono text-[10px] uppercase tracking-widest">
                    // Sample ({targetList.sample_size} of {targetList.total_recipients})
                  </Label>
                  <div className="border border-[#2A0F3D] mt-2 overflow-x-auto">
                    <table className="w-full text-xs font-mono" data-testid="pa-target-sample-table">
                      <thead className="bg-[#050003] text-[#6B8E73]">
                        <tr>
                          <th className="text-left px-2 py-1">Email</th>
                          <th className="text-left px-2 py-1">Name</th>
                          <th className="text-left px-2 py-1">Region</th>
                          <th className="text-right px-2 py-1">Orders</th>
                          <th className="text-left px-2 py-1">Last seen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(targetList.sample || []).map((u) => (
                          <tr key={u.id} className="border-t border-[#2A0F3D]/60 text-[#A3C7A8]">
                            <td className="px-2 py-1 truncate max-w-[240px]">{u.email || '—'}</td>
                            <td className="px-2 py-1 truncate max-w-[160px]">{u.name || '—'}</td>
                            <td className="px-2 py-1">{u.region || '—'}</td>
                            <td className="px-2 py-1 text-right">{u.lifetime_orders}</td>
                            <td className="px-2 py-1 text-[10px]">
                              {u.last_seen_at ? new Date(u.last_seen_at).toLocaleDateString() : '—'}
                            </td>
                          </tr>
                        ))}
                        {(!targetList.sample || targetList.sample.length === 0) && (
                          <tr>
                            <td colSpan={5} className="px-2 py-4 text-center text-[#6B8E73] italic">
                              No matching users — this cohort is empty.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-2 border-t border-[#2A0F3D]">
                  {targetList.list_confirmed_at ? (
                    <div className="text-[#39FF14] font-mono text-xs uppercase tracking-widest flex items-center gap-2 flex-1">
                      <ShieldCheck className="w-4 h-4" />
                      List already confirmed by {targetList.list_confirmed_by || 'operator'}
                    </div>
                  ) : (
                    <Button
                      onClick={confirmTargetList}
                      disabled={confirmingList || (targetList.total_recipients || 0) === 0}
                      className="bg-[#39FF14] text-black hover:bg-[#2bcc10] font-mono text-xs uppercase tracking-wider rounded-none disabled:bg-[#1A1A1A] disabled:text-[#4B6B52]"
                      data-testid="pa-target-confirm"
                    >
                      {confirmingList ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ShieldCheck className="w-3 h-3 mr-1" />}
                      Confirm list & unlock approve
                    </Button>
                  )}
                  <Button
                    onClick={() => (!confirmingList ? setTargetList(null) : null)}
                    variant="ghost"
                    className="text-[#A3C7A8] hover:bg-white/5 font-mono text-xs uppercase tracking-wider"
                    data-testid="pa-target-cancel"
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
